import React,{useState,useEffect} from 'react';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import {invalidateMasterCache} from '../hooks/useMaster';
import {useTranslation} from 'react-i18next';

// Master type labels for display
const TYPE_LABELS:Record<string,string>={
  GENDER:'Gender',BLOOD_GROUP:'Blood Group',MARITAL_STATUS:'Marital Status',
  CONSULTATION_TYPE:'Consultation Type',APPOINTMENT_SOURCE:'Appointment Source',
  PAYMENT_METHOD:'Payment Method',DRUG_ROUTE:'Drug Route',DRUG_FREQUENCY:'Drug Frequency',
  ADMISSION_TYPE:'Admission Type',DISCHARGE_TYPE:'Discharge Type',
  BED_TYPE:'Bed Type',WARD_TYPE:'Ward Type',
  SAMPLE_TYPE:'Sample Type (Lab)',CONTAINER_TYPE:'Container Type (Lab)',
  ANAESTHESIA_TYPE:'Anaesthesia Type',OT_TYPE:'OT Type',
  BLOOD_COMPONENT:'Blood Component',LEAVE_TYPE:'Leave Type',
  EMPLOYEE_TYPE:'Employee Type',DEPARTMENT:'Department',
  RELATIONSHIP:'Relationship',DISCHARGE_CONDITION:'Discharge Condition',
  MODALITY:'Radiology Modality',NURSING_ORDER:'Nursing Order',
  PRIORITY:'Priority Level',SHIFT_TYPE:'Shift Type',
  ARRIVAL_MODE:'Arrival Mode (Emergency)',TRIAGE_LEVEL:'Triage Level',
  DIET_INSTRUCTION:'Diet Instruction',VACCINE_ROUTE:'Vaccine Route',
  EDUCATION:'Education Level',OCCUPATION:'Occupation',
  NATIONALITY:'Nationality',RELIGION:'Religion',
  ENTERPRISE:'Enterprise',COUNTY:'County',SUB_COUNTY:'Sub-county',SERVICE_POINT:'Service delivery point',
};

export default function SystemMastersPage(){
  const {t}=useTranslation();
  const [masters,setMasters]=useState<Record<string,any[]>>({});
  const [selectedType,setSelectedType]=useState('GENDER');
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [search,setSearch]=useState('');
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),3000);};

  const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({masterType:'',masterCode:'',masterValue:'',sortOrder:0,isActive:true,extraData:''});
  const [editId,setEditId]=useState<string|null>(null);

  const load=async()=>{
    setLoading(true);
    try{const r=await api.getMasters();setMasters(r.data||{});}
    catch{}setLoading(false);
  };
  useEffect(()=>{load();},[]);

  const save=async()=>{
    if(!form.masterCode||!form.masterValue){notify('Code and Value are required');return;}
    setSaving(true);
    try{
      const data={...form,masterType:form.masterType||selectedType,extraData:form.extraData?JSON.parse(form.extraData):null};
      if(editId) await api.updateMaster(editId,data);
      else await api.createMaster(data);
      notify('✅ Saved!');setShowForm(false);setEditId(null);
      invalidateMasterCache(form.masterType||selectedType);
      await load();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const deleteMaster=async(id:string,type:string)=>{
    if(!window.confirm('Deactivate this option?'))return;
    await api.deleteMaster(id);
    invalidateMasterCache(type);
    notify('Deactivated');await load();
  };

  const types=Object.keys(masters).sort();
  const currentItems=(masters[selectedType]||[]).filter((m:any)=>
    !search||m.masterValue?.toLowerCase().includes(search.toLowerCase())||m.masterCode?.toLowerCase().includes(search.toLowerCase())
  );

  return(
    <Layout title="System Masters">
      {msg&&<div className="fixed top-4 right-4 bg-white border shadow-lg rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left: type list */}
        <div className="card overflow-hidden">
          <div className="p-3 border-b bg-slate-50">
            <p className="font-semibold text-slate-700 text-sm">Master Types ({types.length})</p>
          </div>
          <div className="max-h-[75vh] overflow-y-auto divide-y">
            {types.map(type=>(
              <button key={type} onClick={()=>{setSelectedType(type);setSearch('');}}
                className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-slate-50 ${selectedType===type?'bg-blue-50 text-[#02357A] font-semibold border-l-2 border-[#015CA6]':''}`}>
                <p className="truncate">{TYPE_LABELS[type]||type.replace(/_/g,' ')}</p>
                <p className="text-xs text-slate-400">{(masters[type]||[]).length} options</p>
              </button>
            ))}
          </div>
        </div>

        {/* Right: options for selected type */}
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="font-semibold text-slate-700">{TYPE_LABELS[selectedType]||selectedType}</h3>
            <input className="input flex-1 min-w-32 text-sm" placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}/>
            <button onClick={()=>{setEditId(null);setForm({masterType:selectedType,masterCode:'',masterValue:'',sortOrder:currentItems.length,isActive:true,extraData:''});setShowForm(true);}} className="btn-primary text-sm whitespace-nowrap">➕ Add Option</button>
          </div>

          <div className="card overflow-hidden">
            <div className="p-3 border-b bg-slate-50 grid grid-cols-12 text-xs font-semibold text-slate-500 uppercase">
              <span className="col-span-2">{t('common.code','Code')}</span>
              <span className="col-span-5">Display Value</span>
              <span className="col-span-2 text-center">Order</span>
              <span className="col-span-2 text-center">{t('common.status','Status')}</span>
              <span className="col-span-1"></span>
            </div>
            <div className="divide-y max-h-[65vh] overflow-y-auto">
              {loading?<div className="p-8 text-center text-slate-400">Loading...</div>
              :currentItems.length===0?<div className="p-8 text-center text-slate-400">No options found</div>
              :currentItems.map((m:any)=>(
                <div key={m.id} className={`grid grid-cols-12 items-center p-3 hover:bg-slate-50 text-sm ${!m.isActive?'opacity-40':''}`}>
                  <span className="col-span-2 font-mono text-xs text-[#02357A] truncate">{m.masterCode}</span>
                  <span className="col-span-5 font-medium">{m.masterValue}</span>
                  <span className="col-span-2 text-center text-slate-400">{m.sortOrder||0}</span>
                  <span className="col-span-2 text-center">
                    <Badge label={m.isActive?'Active':'Inactive'} className={m.isActive?'bg-green-100 text-green-700':'bg-slate-100 text-slate-400'}/>
                  </span>
                  <div className="col-span-1 flex gap-1 justify-end">
                    <button onClick={()=>{setEditId(m.id);setForm({masterType:selectedType,masterCode:m.masterCode,masterValue:m.masterValue,sortOrder:m.sortOrder||0,isActive:m.isActive,extraData:m.extraData?JSON.stringify(m.extraData):''}); setShowForm(true);}} className="text-[#02357A] hover:underline text-xs">{t('common.edit','Edit')}</button>
                    <button onClick={()=>deleteMaster(m.id,selectedType)} className="text-red-500 hover:underline text-xs">Del</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <SlidePanel isOpen={showForm} onClose={()=>setShowForm(false)} title={editId?'Edit Option':'Add Option'}>
        <div className="space-y-3">
          <div>
            <label className="label">Master Type</label>
            <Select value={form.masterType} onChange={e=>setForm({...form,masterType:e.target.value})}>
              {types.map(t=><option key={t} value={t}>{TYPE_LABELS[t]||t}</option>)}
              <option value="__NEW__">+ New Type...</option>
            </Select>
            {form.masterType==='__NEW__'&&<Input className="mt-1" value={form.masterType==='__NEW__'?'':form.masterType} onChange={e=>setForm({...form,masterType:e.target.value.toUpperCase().replace(/\s+/g,'_')})} placeholder="NEW_TYPE_CODE"/>}
          </div>
          <div><label className="label">Code * (unique within type, e.g. MALE, CASH)</label><Input value={form.masterCode} onChange={e=>setForm({...form,masterCode:e.target.value.toUpperCase().replace(/\s+/g,'_')})} placeholder="MASTER_CODE" required/></div>
          <div><label className="label">Display Value *</label><Input value={form.masterValue} onChange={e=>setForm({...form,masterValue:e.target.value})} placeholder="What the user sees" required/></div>
          <div><label className="label">Sort Order</label><Input type="number" value={form.sortOrder} onChange={e=>setForm({...form,sortOrder:+e.target.value})}/></div>
          <div><label className="label">Extra Data (JSON, optional)</label><Input value={form.extraData} onChange={e=>setForm({...form,extraData:e.target.value})} placeholder='{"badge":"MONITORING","daily_rate":500}'/></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={e=>setForm({...form,isActive:e.target.checked})} className="w-4 h-4"/> Active</label>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="btn-primary flex-1">{saving?'Saving...':'Save'}</button>
            <button onClick={()=>setShowForm(false)} className="btn-secondary flex-1">{t('common.cancel','Cancel')}</button>
          </div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
